// Placar ICPC. Um só componente serve a equipe, o admin e o telão — muda
// apenas a densidade (`size`), para o telão ficar legível de longe.
//
// Regras do DESIGN.md (seção 7) que não são cosméticas:
//   - sem verde para resolvido nem amarelo para pendente; a paleta é a do tema;
//   - a célula distingue os quatro estados por CONTEÚDO e ÍCONE, não só por
//     cor — quem não distingue matizes precisa ler o placar igual;
//   - números vêm do servidor; nada é recalculado aqui;
//   - durante o congelamento, pendente não revela veredito nenhum.

import { Check, Lock, Minus, X } from "lucide-react";
import type { ScoreboardCell, ScoreboardDto } from "@compila/shared";
import { Identicon } from "./Identicon.js";

interface Props {
  board: ScoreboardDto;
  size?: "normal" | "big";
  /** Destaca a linha da própria equipe. */
  highlightTeamId?: string;
  /** Quando congelado, a célula pendente é "escondido", não "julgando". */
  frozen?: boolean;
}

export function Scoreboard({
  board,
  size = "normal",
  highlightTeamId,
  frozen = false,
}: Props): React.JSX.Element {
  const big = size === "big";

  if (board.rows.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        Nenhuma equipe cadastrada ainda.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <caption className="sr-only">
          Classificação: posição, equipe, problemas resolvidos, penalidade e situação
          por problema.
        </caption>
        <thead>
          <tr
            className={`border-b border-border text-muted-foreground ${
              big ? "text-lg" : "text-xs"
            }`}
          >
            <th scope="col" className="w-14 px-2 py-3 text-right font-medium">
              #
            </th>
            <th scope="col" className="px-3 py-3 text-left font-medium">
              Equipe
            </th>
            <th scope="col" className="w-20 px-2 py-3 text-center font-medium">
              Resolv.
            </th>
            <th scope="col" className="w-24 px-2 py-3 text-center font-medium">
              Penal.
            </th>
            {board.problems.map((p) => (
              <th
                key={p.id}
                scope="col"
                className={`px-1 py-3 text-center font-semibold text-foreground ${
                  big ? "w-28" : "w-20"
                }`}
              >
                {p.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {board.rows.map((row) => {
            const mine = row.teamId === highlightTeamId;
            return (
              <tr
                key={row.teamId}
                className={[
                  "border-b border-border transition-colors",
                  mine ? "bg-accent/5" : "hover:bg-background/60",
                  row.official ? "" : "opacity-70",
                  big ? "text-2xl" : "text-sm",
                ].join(" ")}
              >
                <td className="px-2 py-2 text-right font-semibold tabular-nums">
                  {row.rank ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex min-w-0 items-center gap-3">
                    <Identicon
                      seed={row.teamId}
                      className={big ? "size-9" : "size-7"}
                    />
                    <span className="truncate font-medium" title={row.teamName}>
                      {row.teamName}
                    </span>
                    {!row.official && (
                      <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                        fora de competição
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2 text-center font-semibold tabular-nums">
                  {row.solved}
                </td>
                <td className="px-2 py-2 text-center tabular-nums text-muted-foreground">
                  {row.penalty}
                </td>
                {board.problems.map((p) => (
                  <td key={p.id} className="px-1 py-1">
                    <Cell cell={row.cells[p.id]} big={big} frozen={frozen} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Cell({
  cell,
  big,
  frozen,
}: {
  cell: ScoreboardCell | undefined;
  big: boolean;
  frozen: boolean;
}) {
  const base = `flex flex-col items-center justify-center gap-0.5 rounded-lg border ${
    big ? "h-16 text-lg" : "h-11 text-xs"
  }`;
  const icone = big ? 18 : 13;

  if (!cell || cell.state === "NONE") {
    return (
      <div
        className={`${base} border-border/50 bg-background/40 text-muted-foreground`}
        title="Sem tentativas"
      >
        <Minus size={icone} aria-hidden />
        <span className="sr-only">Sem tentativas</span>
      </div>
    );
  }

  if (cell.state === "SOLVED") {
    const texto = `Resolvido no minuto ${cell.minute}${
      cell.wrong > 0 ? `, com ${cell.wrong} tentativa(s) errada(s)` : ""
    }`;
    return (
      <div
        className={`${base} border-accent/40 bg-accent/10 font-semibold text-accent`}
        title={texto}
      >
        <span className="flex items-center gap-1 tabular-nums">
          <Check size={icone} aria-hidden />
          {cell.minute}
        </span>
        {cell.wrong > 0 && (
          <span className={`font-normal ${big ? "text-sm" : "text-[10px]"}`}>
            +{cell.wrong}
          </span>
        )}
        <span className="sr-only">{texto}</span>
      </div>
    );
  }

  if (cell.state === "PENDING") {
    // Congelado e "em julgamento" são coisas diferentes para quem assiste, mas
    // ambos significam "ainda não se sabe" — e nenhum revela veredito.
    const texto = frozen
      ? `${cell.pending} submissão(ões) ocultas pelo congelamento`
      : `${cell.pending} submissão(ões) em julgamento`;
    return (
      <div
        className={`${base} border-accent/40 bg-accent/5 font-semibold text-accent`}
        title={texto}
      >
        <span className="flex items-center gap-1">
          <Lock size={icone} aria-hidden />
          {cell.pending}
        </span>
        <span className={`font-normal ${big ? "text-sm" : "text-[10px]"}`}>
          {frozen ? "oculto" : "julgando"}
        </span>
        <span className="sr-only">{texto}</span>
      </div>
    );
  }

  const texto = `${cell.wrong} tentativa(s) sem solução`;
  return (
    <div
      className={`${base} border-destructive/40 bg-destructive/10 font-semibold text-destructive`}
      title={texto}
    >
      <span className="flex items-center gap-1 tabular-nums">
        <X size={icone} aria-hidden />
        {cell.wrong}
      </span>
      <span className="sr-only">{texto}</span>
    </div>
  );
}
